<template>
  <AppModal
    modalId="epedidoDetalleModal"
    :showModal="!!pedido"
    :title="`E-Pedido #${pedido?.pedido_online_id ?? ''}`"
    dialogClass="modal-lg"
    withBackdrop
    @close="$emit('close')"
  >
    <template #body>
      <div v-if="pedido">
        <!-- Estado -->
        <div class="d-flex align-items-center gap-2 mb-3">
          <span :class="['badge', claseEstado]">{{ etiquetaEstado }}</span>
          <small class="text-muted">Recibido el {{ formatearFecha(pedido.creado_en) }}</small>
        </div>

        <div v-if="esDescartado" class="alert alert-secondary py-2 mb-3">
          <div class="fw-semibold">Descartado</div>
          <div>{{ pedido.descartado_motivo || 'Sin motivo registrado' }}</div>
          <small class="text-muted">
            {{ pedido.descartado_por_nombre ? `Por ${pedido.descartado_por_nombre} · ` : '' }}
            {{ formatearFecha(pedido.descartado_en) }}
          </small>
        </div>

        <div v-if="esAtendido" class="alert alert-success py-2 mb-3">
          Atendido como pedido interno
          <strong>#{{ pedido.pedido_id }}</strong>.
          <router-link class="alert-link" to="/gestion-pedidos" @click="$emit('close')">
            Ver en Pedidos
          </router-link>
        </div>

        <!-- Cliente -->
        <h3 class="h6 text-uppercase text-muted mb-2">Cliente</h3>
        <div class="row g-2 mb-3">
          <div class="col-md-6"><strong>{{ pedido.contacto?.nombre || 'Cliente ecommerce' }}</strong></div>
          <div class="col-md-6">Documento: {{ pedido.contacto?.numero_documento || '—' }}</div>
          <div class="col-md-6">Teléfono: {{ pedido.contacto?.telefono || '—' }}</div>
          <div class="col-md-6">Email: {{ pedido.contacto?.email || '—' }}</div>
        </div>

        <!-- Entrega -->
        <h3 class="h6 text-uppercase text-muted mb-2">Entrega</h3>
        <div class="mb-3">
          <div><strong>{{ esEnvio ? 'Envío a domicilio' : 'Retiro en el local' }}</strong></div>
          <template v-if="esEnvio">
            <div>{{ direccionTexto || 'Sin dirección cargada' }}</div>
            <div v-if="pedido.envio?.barrio" class="text-muted">Barrio: {{ pedido.envio.barrio }}</div>
            <div v-if="pedido.envio?.referencia" class="text-muted small">
              Referencia: {{ pedido.envio.referencia }}
            </div>
            <a
              v-if="enlaceMapa"
              :href="enlaceMapa"
              target="_blank"
              rel="noopener"
              class="small"
            >
              <i class="bi bi-geo-alt"></i> Ver ubicación en el mapa
            </a>
          </template>
        </div>

        <!-- Líneas -->
        <h3 class="h6 text-uppercase text-muted mb-2">Detalle del pedido</h3>
        <div class="table-responsive">
          <table class="table table-sm align-middle mb-2">
            <thead>
              <tr>
                <th>Producto</th>
                <th class="text-end">Cantidad</th>
                <th class="text-end">Precio</th>
                <th class="text-end">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!lineas.length">
                <td colspan="4" class="text-center text-muted py-3">Este pedido no tiene líneas.</td>
              </tr>
              <tr v-for="(linea, i) in lineas" :key="i">
                <td>
                  {{ linea.nombre || `Producto #${linea.producto_id}` }}
                  <span v-if="linea.es_granel" class="badge text-bg-light ms-1">granel</span>
                </td>
                <td class="text-end">{{ cantidadTexto(linea) }}</td>
                <td class="text-end">{{ precioTexto(linea) }}</td>
                <td class="text-end">{{ formatearMonto(subtotalLinea(linea)) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <th colspan="3" class="text-end">Total</th>
                <th class="text-end">{{ formatearMonto(pedido.subtotal) }}</th>
              </tr>
            </tfoot>
          </table>
        </div>

        <small v-if="totalDifiere" class="text-warning">
          <i class="bi bi-exclamation-triangle"></i>
          La suma de las líneas ({{ formatearMonto(totalLineas) }}) no coincide con el total
          guardado. Conviene revisarlo antes de atender.
        </small>
      </div>
    </template>
    <template #footer>
      <button type="button" class="btn btn-outline-secondary" @click="$emit('close')">Cerrar</button>
    </template>
  </AppModal>
</template>

<script>
import AppModal from './AppModal.vue'

// Detalle de un E-Pedido (RF-5 de SPEC_EPEDIDOS_GESTION_v0.1.md).
//
// Antes la lista mostraba sólo la CANTIDAD de ítems, así que el operador
// atendía o descartaba sin ver qué había pedido el cliente. Para descartar eso
// era especialmente grave: se rechazaba un pedido a ciegas.
//
// Componente propio y no una adaptación de PedidoDetalleModal (D1 del PLAN):
// aquel espera la forma de un pedido INTERNO (`detalles` con `detalle_id`),
// mientras que un e-pedido guarda sus líneas como JSONB en `items`, con otra
// forma y otra unidad. Mapear una en la otra acoplaría dos módulos que hoy
// están sanos y separados.
//
// No hace ningún request: la lista ya trae `items`, `envio` y los campos de
// descarte con su `SELECT *` (D2), así que el modal se abre con la fila que ya
// está en memoria.
export default {
  name: 'EPedidoDetalleModal',
  components: { AppModal },
  props: {
    pedido: { type: Object, default: null }
  },
  emits: ['close'],
  computed: {
    lineas() {
      return Array.isArray(this.pedido?.items) ? this.pedido.items : []
    },
    esEnvio() {
      return this.pedido?.modo_entrega === 'ENVIO'
    },
    esDescartado() {
      return this.pedido?.estado_importacion === 'descartado'
    },
    esAtendido() {
      return this.pedido?.estado_importacion === 'importado'
    },
    etiquetaEstado() {
      if (this.esAtendido) return 'Atendido'
      if (this.esDescartado) return 'Descartado'
      return 'Pendiente'
    },
    claseEstado() {
      if (this.esAtendido) return 'text-bg-success'
      if (this.esDescartado) return 'text-bg-secondary'
      return 'text-bg-warning'
    },
    direccionTexto() {
      const envio = this.pedido?.envio
      if (!envio) return ''
      // Mismo criterio que `direccionTexto` del backend y que el mensaje de
      // WhatsApp del storefront: nunca una parte vacía dentro del texto.
      return [
        envio.calle_principal,
        envio.calle_transversal ? `esq. ${envio.calle_transversal}` : null,
        envio.numero ? `N° ${envio.numero}` : null
      ].filter(Boolean).join(', ')
    },
    enlaceMapa() {
      const { lat, lng } = this.pedido?.envio || {}
      if (lat == null || lng == null) return null
      return `https://www.google.com/maps?q=${lat},${lng}`
    },
    totalLineas() {
      return this.lineas.reduce((suma, linea) => suma + this.subtotalLinea(linea), 0)
    },
    // El total guardado y la suma de las líneas deberían coincidir. Si no,
    // vale avisarlo en vez de mostrar dos números distintos sin comentario:
    // es justamente el tipo de inconsistencia que conviene ver ANTES de
    // atender el pedido.
    totalDifiere() {
      const guardado = Number(this.pedido?.subtotal)
      if (!Number.isFinite(guardado)) return false
      return Math.abs(guardado - this.totalLineas) > 1
    }
  },
  methods: {
    // Una línea de granel se muestra en KILOGRAMOS, que es como la compró el
    // cliente y como está guardada en `items`. La conversión a gramos ocurre
    // recién al atender, en el puente hacia el pedido interno.
    cantidadTexto(linea) {
      const cantidad = Number(linea?.cantidad) || 0
      if (linea?.es_granel) {
        return `${cantidad.toLocaleString('es-PY', { maximumFractionDigits: 3 })} kg`
      }
      return `${cantidad} un`
    },
    precioTexto(linea) {
      const precio = this.formatearMonto(linea?.precio_venta)
      return linea?.es_granel ? `${precio}/kg` : precio
    },
    subtotalLinea(linea) {
      return (Number(linea?.cantidad) || 0) * (Number(linea?.precio_venta) || 0)
    },
    formatearMonto(monto) {
      const numero = Number(monto)
      if (!Number.isFinite(numero)) return '0 ₲'
      return `${Math.round(numero).toLocaleString('es-PY', { minimumFractionDigits: 0 })} ₲`
    },
    formatearFecha(valor) {
      if (!valor) return '—'
      const fecha = new Date(valor)
      if (Number.isNaN(fecha.getTime())) return '—'
      return `${fecha.toLocaleDateString('es-PY')} ${fecha.toLocaleTimeString('es-PY', {
        hour: '2-digit',
        minute: '2-digit'
      })}`
    }
  }
}
</script>
